import Debug from 'debug';
import Header from '@atlas/atlas-communication-header';
import pako from 'pako';

// const Debug = require('debug');
// const Header = require('@atlas/atlas-communication-header');
// const pako = require('pako');

/* eslint-disable func-names, max-len */
const debug = Debug('AtlasHttp:Lib');
const verbose = Debug('Verbose:AtlasHttp:Lib');
const maxLoadTimeDefaultSec = (20 * 60); // 20 minutes
const AtlasHeader = new Header.Header();
const CorrelationHeaderName = 'X-Siemens-Correlation-Id';
const clientVersionHeaderName = 'x-atlas-httpclient-version';
const clientVersion = 5;
const dataTypeJson = 'json';
const dataTypeAtlasBinary = 'atlas-binary';
const dataTypeBinary = 'binary';
const maxChunkSize = 4194304; // Maximum chunk size (approx. 4 MB), used for range requests in JRM getData

function AtlasHttp(options = {}) {
  this.options = options;
  if (!this.options.maxLoadTimeSec) this.options.maxLoadTimeSec = maxLoadTimeDefaultSec;
  this.validHttpStatus = [200, 202, 204, 206];
  if (!this.options.dataType) this.options.dataType = dataTypeAtlasBinary;
  const validDataTypes = [dataTypeAtlasBinary, dataTypeJson, dataTypeBinary];
  if (!validDataTypes.includes(this.options.dataType)) {
    this.options.dataType = dataTypeAtlasBinary;
  }
  if (!this.options.jrm || !this.options.jrm.baseUrl) {
    this.options.useJrm = false;
  }
  else {
    this.options.useJrm = true;
  }
}

AtlasHttp.prototype.makeParam = function (k, v) {
  return { key: k, value: v };
};

AtlasHttp.prototype.encodeParams = (p) => p
  .filter((kvp) => kvp.key && kvp.value)
  .map((kvp) => `${kvp.key}=${encodeURIComponent(kvp.value)}`)
  .join('&');

AtlasHttp.prototype.isArrayBuffer = function (value) {
  return value && value.buffer instanceof ArrayBuffer && value.byteLength !== undefined;
};

AtlasHttp.prototype.isString = function (s) {
  return (typeof s === 'string' || s instanceof String);
};

/**
 * Parses a 'Content-Range' header to extract range details.
 *
 * @param {string} range - The 'Content-Range' header string in the format 'bytes start-end/total'.
 * @returns {Object} - An object containing:
 *   - `dataReceived` (number): The size of the data received, calculated as (end - start + 1).
 *   - `totalSize` (number): The total size of the resource as specified in the header.
 * @throws {Error} - Throws an error if the range format is invalid or contains non-numeric values.
 *
 * Example usage:
 *   const result = parseRangeHeader('bytes 0-499/1234');
 *   // result: { dataReceived: 500, totalSize: 1234 }
 */
AtlasHttp.prototype.parseRangeHeader = function (range) {
  try {
    if (range.slice(0, 5) !== 'bytes') {
      throw new Error('Invalid range: only supporting bytes');
    }
    const rangeParts = range.slice(6).split('/'); // Remove 'bytes ' and split by '/'
    if (rangeParts.length !== 2) {
      throw new Error("Invalid range format: Missing '/' separator");
    }

    const [rangeSpan, totalSizeStr] = rangeParts;
    const rangeBounds = rangeSpan.split('-'); // Split the range span "0-499"
    if (rangeBounds.length !== 2) {
      throw new Error("Invalid range format: Range span must be 'start-end'");
    }

    // Parse the start, end, and total size
    const start = parseInt(rangeBounds[0], 10);
    const end = parseInt(rangeBounds[1], 10);
    const totalSize = parseInt(totalSizeStr, 10);

    if (Number.isNaN(start) || Number.isNaN(end) || Number.isNaN(totalSize)) {
      throw new Error('Invalid range format: Non-numeric values found');
    }

    // Calculate data received
    const dataReceived = end - start + 1;

    return { dataReceived, totalSize };
  }
  catch (error) {
    console.error('Error parsing range header:', error);
    throw new Error(`Invalid 'ContentRange' Header:  ${error.message}`);
  }
};

/* eslint-disable */
AtlasHttp.prototype.uuidv4 = function () {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
}
/* eslint-enable */
/* eslint-disable func-names, max-len */

AtlasHttp.prototype.getHeaderSize = (d) => AtlasHeader.getBufferSize(d);
AtlasHttp.prototype.decodeHeader = (d) => AtlasHeader.decodeHeader(d);

AtlasHttp.prototype.getUrl = function (action, params = []) { // , urlOptions = {}) {
  // const { protocol } = window.location;
  // let { host } = window.location;

  // if (urlOptions.host) {
  //   ({ host } = urlOptions);
  // }

  // encode url parameters
  const urlPart = this.options.service ? `/${this.options.service}` : '';
  const url = `${urlPart}${action}?${this.encodeParams(params)}`;
  return url;
};

/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["xhttp"] }] */
AtlasHttp.prototype.setupRequest = function (api, xhttp, body, headers) {
  let corrIdUsed = false;
  let contentTypeUsed = false;

  headers.forEach((h) => {
    if (h.name && h.value && this.isString(h.name) && this.isString(h.value)) {
      if (h.name.toLowerCase() === 'cors-with-credentials') {
        if (h.value.toLowerCase() === 'true') {
          xhttp.withCredentials = true;
          if (console && console.log) {
            console.log('Using withCredentials option in xhr');
          }
        }
      }
      else {
        xhttp.setRequestHeader(h.name, h.value);
        if (h.name.toLowerCase() === 'x-siemens-correlation-id') {
          corrIdUsed = true;
          debug(`Using passed correlation id in ${api} call`, h.value);
        }
        if (h.name.toLowerCase() === 'content-type') contentTypeUsed = true;
      }
    }
  });
  if (body && !contentTypeUsed) {
    if (this.isArrayBuffer(body)) xhttp.setRequestHeader('Content-Type', 'application/octet-stream');
    else xhttp.setRequestHeader('Content-Type', 'application/json');
  }
  if (!corrIdUsed) {
    const newCorrId = this.uuidv4();
    xhttp.setRequestHeader(CorrelationHeaderName, newCorrId);
    debug(`Generating correlation id in ${api} call`, newCorrId);
    if (console && console.log) {
      console.log(`Generating correlation id in ${api} call`, newCorrId);
    }
  }
  xhttp.setRequestHeader(clientVersionHeaderName, clientVersion);
};

/*
* headers parameter is an array of object of type {name: "", value: ""}
*/
AtlasHttp.prototype.sendApi = function (action, body, params = [], headers = [], timeoutSec = 30) {
  const self = this;
  return new Promise((resolve, reject) => {
    const timerId = setTimeout(() => reject(new Error(`Timeout in waiting for response from ${action}`)), timeoutSec * 1000);
    const xhttp = new XMLHttpRequest();
    const url = self.getUrl(action, params);
    xhttp.open('POST', url, true);
    self.setupRequest('sendApi', xhttp, body, headers);
    // let corrIdUsed = false;
    // let contentTypeUsed = false;
    // headers.forEach((h) => {
    //   if (h.name && h.value && self.isString(h.name) && self.isString(h.value)) {
    //     xhttp.setRequestHeader(h.name, h.value);
    //     if (h.name.toLowerCase() === 'x-siemens-correlation-id') {
    //       corrIdUsed = true;
    //       debug('Using passed correlation id in sendApi call', h.value);
    //     }
    //     if (h.name.toLowerCase() === 'content-type') contentTypeUsed = true;
    //   }
    // });
    // if (!contentTypeUsed) {
    //   if (self.isArrayBuffer(body)) xhttp.setRequestHeader('Content-Type', 'application/octet-stream');
    //   else xhttp.setRequestHeader('Content-Type', 'application/json');
    // }
    // if (!corrIdUsed) {
    //   const newCorrId = self.uuidv4();
    //   xhttp.setRequestHeader(CorrelationHeaderName, newCorrId);
    //   debug('Generating correlation id in sendApi call', newCorrId);
    //   if (console && console.log) {
    //     console.log('Generating correlation id in sendApi call', newCorrId);
    //   }
    // }
    xhttp.onload = function () {
      if (xhttp.status >= 200 && xhttp.status < 300) {
        clearTimeout(timerId);
        resolve({ status: xhttp.status, response: xhttp.response });
      }
      else {
        clearTimeout(timerId);
        reject(new Error(xhttp.responseText));
      }
    };
    xhttp.onerror = function (event) {
      clearTimeout(timerId);
      if (console && console.log) {
        console.log('onError handler', event);
      }
      reject(new Error(`${xhttp.status} ${xhttp.statusText} - Network error in sendApi`));
    };
    // Note: JSON data must already be stringified before calling sendApi
    xhttp.send(body);
  });
};

/**
 * Sends a GET request using the Fetch API.
 *
 * Example usage:
 * ```
 * const response = await atlasHttp.getApi('/api/get-data',  <headers> eg. [{ name: 'Authorization', value: 'Bearer token' }]);
 * console.log(response);
 * ```
 */
AtlasHttp.prototype.getApi = async function (action, headers = []) {
  const url = this.getUrl(action);
  try {
    // Use the Fetch API to send a GET request
    const response = await fetch(url, {
      method: 'GET',
      headers: this.prepareHeaders(headers),
      credentials: this.getFetchCredentialsValue(headers),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error: ${response.status} - ${errorText}`);
    }
    const responseData = await response.text();
    return { status: response.status, response: responseData };
  }
  catch (error) {
    console.error(`Error in getApi (action: ${action}, url: ${url}):, ${error}`);
    throw error;
  }
};

/*
    This function is called to retrieve data using the Atlas Service response pattern
    204 -> no data available
    206- > partial data sent, more data available
    200 -> all data sent

    jobId is a mandatory parameter. Direct parameter dont pass it as part of params
    LastData-Id parameter sent in response must be sent as query parameter to the next call
*/
AtlasHttp.prototype.fetchData = async function (action, jobId, params, headers, body = null) {
  const url = this.constructUrl(action, jobId, params);
  const reqHeaders = this.prepareHeaders(headers, body);
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: reqHeaders,
      credentials: this.getFetchCredentialsValue(headers),
    });
    let setResponseType = this.options.dataType === dataTypeAtlasBinary || this.options.dataType === dataTypeBinary ? 'arraybuffer' : 'text';
    // Check if Accept header is 'application/octet-stream'
    const acceptHeader = headers.find((header) => header.name.toLowerCase() === 'accept');
    if (this.options.useJrm && acceptHeader && acceptHeader.value === 'application/octet-stream') {
      setResponseType = 'arraybuffer';
      console.debug('Accept header is application/octet-stream');
    }
    return await this.processResponse(response, setResponseType);
  }
  catch (error) {
    console.error('Fetch API error:', error);
    throw error;
  }
};

AtlasHttp.prototype.constructUrl = function (action, jobId, params) {
  if (this.options.useJrm) {
    let url = `${this.options.jrm.baseUrl}/job/${jobId}/data`;
    if (params && params.length > 0) {
      url += `?${this.encodeParams(params)}`;
    }
    return url;
  }
  params.push(this.makeParam('id', jobId));
  params.push(this.makeParam('jobId', jobId));
  return this.getUrl(action, params);
};

/*
* headers parameter is an array of object of type {name: "", value: ""}
*/
AtlasHttp.prototype.getFetchCredentialsValue = function (headers) {
  // Include credentials if requested for CORS, otherwise use 'same-origin' for same-origin requests;
  return headers.some((h) => h.name.toLowerCase() === 'cors-with-credentials' && h.value.toLowerCase() === 'true') ? 'include' : 'same-origin';
};

AtlasHttp.prototype.setupFetchRequest = function (api, reqHeaders, headers, body) {
  let corrIdUsed = false;
  let contentTypeUsed = false;

  headers.forEach((h) => {
    if (h.name && h.value && this.isString(h.name) && this.isString(h.value)) {
      if (h.name.toLowerCase() === 'cors-with-credentials') {
        // don't set this header in fetch request as this is an internal special header in atlas-httpclient
        // and it is handled in the get/post calls directly by calling getFetchCredentialsValue function.
        console.log(`Using withCredentials option in fetch. Value: ${h.value}`);
      }
      else {
        reqHeaders.append(h.name, h.value);
        if (h.name.toLowerCase() === 'x-siemens-correlation-id') {
          corrIdUsed = true;
          debug(`Using passed correlation id in ${api} call`, h.value);
        }
        if (h.name.toLowerCase() === 'content-type') contentTypeUsed = true;
      }
    }
  });

  if (body && !contentTypeUsed) {
    if (this.isArrayBuffer(body)) reqHeaders.append('Content-Type', 'application/octet-stream');
    else reqHeaders.append('Content-Type', 'application/json');
  }

  if (!corrIdUsed) {
    const newCorrId = this.uuidv4();
    reqHeaders.append(CorrelationHeaderName, newCorrId);
    debug(`Generating correlation id in ${api} call`, newCorrId);
    if (console && console.log) {
      console.log(`Generating correlation id in ${api} call`, newCorrId);
    }
  }
  reqHeaders.append(clientVersionHeaderName, clientVersion);
};

AtlasHttp.prototype.prepareHeaders = function (headers, body) {
  const reqHeaders = new Headers();
  this.setupFetchRequest('poll', reqHeaders, headers, body);
  return reqHeaders;
};

AtlasHttp.prototype.processResponse = async function (response, setResponseType) {
  const httpRes = {
    status: response.status,
    nextId: response.headers.get('Lastdata-Id') || response.headers.get('LastData-Id'),
    pagesReceived: parseInt(response.headers.get('Pages-Sent') || '0', 10),
    totalPages: parseInt(response.headers.get('Total-Pages') || '0', 10),
    pagesArray: JSON.parse(response.headers.get('Pages-Array') || '{}'),
    data: null,
  };

  if (this.options.useJrm) {
    httpRes.pageInfo = response.headers.get('Add-Info') ? JSON.parse(response.headers.get('Add-Info')) : null;
    httpRes.xJobContentType = response.headers.get('x-job-content-type') || null;
    if (response.status === 206) {
      httpRes.contentRange = response.headers.get('Content-Range');
    }
  }
  else {
    httpRes.pageInfo = response.headers.get('Load-Status') ? JSON.parse(response.headers.get('Load-Status')) : null;
  }

  if (response.headers.get('Pages-Array')) {
    try {
      httpRes.pagesArray = JSON.parse(response.headers.get('Pages-Array'));
    }
    catch (error) {
      httpRes.pagesArray = [];
      console.error('Error parsing Pages-Array http header.', error);
    }
  }
  verbose(httpRes);

  if (this.validHttpStatus.includes(response.status)) {
    if (response.status === 200 || response.status === 206) {
      if (setResponseType === 'arraybuffer') {
        // Convert the response to an ArrayBuffer
        httpRes.data = await response.arrayBuffer();
      }
      else if (setResponseType === 'text') {
        httpRes.data = await response.text();
      }
    }
    else if (response.status === 202 || response.status === 204) {
      httpRes.data = null;
    }
    return httpRes;
  // eslint-disable-next-line no-else-return
  }

  let errorText = null;

  if (response.headers.get('content-type') === 'application/octet-stream') {
    // Handle binary data
    const errorBuffer = await response.arrayBuffer();
    errorText = new TextDecoder('utf-8').decode(errorBuffer);
  }
  else {
    errorText = await response.text();
  }
  console.error((`Failure response from server. ${errorText}`));
  throw new Error(`Failure response from server. ${errorText}`);
};

function mergeArrayBuffers(bufferArray) {
  // Calculate total byte length
  const totalLength = bufferArray.reduce((sum, buf) => sum + buf.byteLength, 0);

  // Create a new ArrayBuffer to hold the merged data
  const mergedBuffer = new Uint8Array(totalLength);

  // Copy each buffer into the new merged buffer
  let offset = 0;
  bufferArray.forEach((buf) => {
    mergedBuffer.set(new Uint8Array(buf), offset);
    offset += buf.byteLength;
  });

  return mergedBuffer.buffer; // Convert Uint8Array back to ArrayBuffer
}

/**
 * Fetches data in parallel using HTTP range requests and combines it into a single response.
 * Handles data >6MB by dividing it into chunks and fetching them concurrently.
 * If any fetch operation fails (after retries), the entire operation is aborted.
 *
 * @async
 * @function
 * @param {string} action - The API endpoint to be called for fetching data.
 * @param {string} jobId - The jobId
 * @param {Object} params - Additional parameters for the request.
 * @param {Object} headers - HTTP Headers
 * @param {any} firstchunk - The already-received initial chunk of data.
 * @param {string} contentRange - The `Content-Range` header indicating total size and received data.
 * @returns {Promise<Object>} - Combined data and headers from the final chunk.
 * @throws {Error} - Throws an error if any chunk fetch fails after retries.
 */
AtlasHttp.prototype.getMultichunkData = async function (action, jobId, params, headers, firstchunk, contentRange) {
  const self = this;
  console.debug(`Range header received ${contentRange}`);
  const { dataReceived, totalSize } = self.parseRangeHeader(contentRange);

  /** Calculate the number of chunks needed to process the remaining data:
   * (totalSize - dataReceived) gives the remaining data to process.
   * Dividing by maxChunkSize determines how many full chunks are needed.
   * Math.ceil ensures any leftover data (remainder) gets an additional chunk.
   * Adding 1 accounts for the already-received chunk
   */
  const numChunks = Math.ceil((totalSize - dataReceived) / maxChunkSize) + 1;

  // Initialize storage for all chunks
  const combinedData = new Array(numChunks);
  combinedData[0] = firstchunk; // Store the first received chunk
  let lastChunkResult = {}; // Stores headers from the final fetch

  /**
   * Fetches a single chunk of data with retry logic.
   * @param {number} start - Start byte position.
   * @param {number} end - End byte position.
   * @param {number} index - Index in the `combinedData` array.
   */
  async function fetchChunk(start, end, index) {
    const startTime = performance.now(); // Record the start time
    try {
      const rangeHeaders = [...headers, { name: 'Range', value: `bytes=${start}-${end}` }];
      console.debug(`Fetching chunk ${index}: bytes ${start}-${end}`);
      console.debug(`url ${action} With header ${rangeHeaders}`);
      const httpResult = await self.fetchData(action, jobId, params, rangeHeaders);

      if ([200, 206].includes(httpResult.status)) {
        combinedData[index] = httpResult.data; // Save the fetched chunk
        if (httpResult.status === 200) {
          lastChunkResult = httpResult; // Save headers for the full response
        }
        return;
      }
      throw new Error(`Unexpected status code: ${httpResult.status}`);
    }
    catch (err) {
      console.error(`Failed to fetch chunk [${start}-${end}] : ${err}`);
      throw new Error(`Failed to fetch chunk [${start}-${end}] ${err.message}`);
    }
    finally {
      const endTime = performance.now(); // Record the end time
      console.log(`fetchChunk(${start}, ${end}, ${index}) took ${(endTime - startTime).toFixed(2)} ms`);
    }
  }

  // Generate fetch promises for all chunks
  const fetchPromises = [];
  let start = dataReceived;

  for (let i = 1; i < numChunks; i += 1) {
    // Calculate the end index for the current chunk
    // - The `start + maxChunkSize - 1` ensures that we don't exceed the maximum chunk size
    // - The `Math.min()` ensures that we don't go beyond the total size of the data (totalSize - 1)
    const end = Math.min(start + maxChunkSize - 1, totalSize - 1);
    console.debug(`chunk no = ${i} ${start}-${end}`);
    fetchPromises.push(fetchChunk(start, end, i));
    // Update the `start` index for the next chunk
    // The next chunk will start from the position right after the current chunk's `end`
    start = end + 1;
  }

  // Execute all fetches in parallel
  try {
    await Promise.all(fetchPromises);
  }
  catch (err) {
    console.error(`Data chunk fetch failed: ${err.message}`);
    throw new Error(`Failed to fetch all chunks: ${err.message}`);
  }

  // Merge all chunks and preserve headers from the last chunk
  if (typeof lastChunkResult.data === 'string') {
    console.debug('Merging string chunks');
    lastChunkResult.data = combinedData.join(''); // Merge string chunks
  }
  else if (lastChunkResult.data instanceof ArrayBuffer) {
    console.debug('Merging ArrayBuffer chunks');
    lastChunkResult.data = mergeArrayBuffers(combinedData); // Merge ArrayBuffer chunks
  }
  else {
    console.warn('Unsupported data type for merging:', typeof lastChunkResult.data);
  }

  return lastChunkResult;
};

AtlasHttp.prototype.processJsonData = function (jobId, data, pagesArray, totalPages) {
  try {
    if (this.options.onJsonData) {
      const jsonData = JSON.parse(data);
      this.options.onJsonData(jobId, jsonData, pagesArray, totalPages);
    }
  }
  catch (error) {
    debug('=== Error processing json data===', error);
    throw error;
  }
};

AtlasHttp.prototype.processTextData = function (jobId, data, pagesArray, totalPages) {
  try {
    if (this.options.onTextData) {
      this.options.onTextData(jobId, data, pagesArray, totalPages);
    }
  }
  catch (error) {
    debug('=== Error processing text data===', error);
    throw error;
  }
};

AtlasHttp.prototype.processBinaryData = function (jobId, data, pagesArray, totalPages) {
  try {
    if (this.options.onBinaryData) {
      this.options.onBinaryData(jobId, data, pagesArray, totalPages);
    }
  }
  catch (error) {
    debug('=== Error processing binary data===', error);
    throw error;
  }
};

AtlasHttp.prototype.processAtlasBinaryData = function (jobId, data) {
  try {
    let moreData = true;
    let byteArray = data;
    while (moreData) {
      if (byteArray.byteLength < 16) {
        debug('Not enough data to parse the header');
        break;
      }

      const header = this.decodeHeader(byteArray);
      const headerSize = this.getHeaderSize(byteArray);
      const msgSize = header.getSize();
      const nextSegmentOffset = headerSize + msgSize;
      if (byteArray.byteLength < nextSegmentOffset) {
        debug(`Not enough data to generate graphics, require more ${nextSegmentOffset - byteArray.byteLength} bytes!!`);
        break;
      }

      const body = new Uint8Array(byteArray.slice(headerSize, nextSegmentOffset));
      let inflate = body;

      if (header.isCompress()) {
        inflate = pako.inflate(body);
      }

      if (header.isGraphicsStructure() && this.options.onStructure) {
        this.options.onStructure(jobId, inflate, header);
      }
      else if (header.isGraphicsGeometry() && this.options.onGeometry) {
        this.options.onGeometry(jobId, inflate, header);
      }

      byteArray = byteArray.slice(nextSegmentOffset);
      moreData = byteArray.byteLength !== 0;
    }
  }
  catch (error) {
    debug('=== Error processing atlas binary data===', error);
    throw error;
  }
};

AtlasHttp.prototype.processHttpData = function (jobId, data, pagesArray, totalPages) {
  debug('process http data called', typeof data, data, pagesArray, totalPages);
  if (typeof data === 'string') {
    if (this.options.dataType === dataTypeJson) {
      this.processJsonData(jobId, data, pagesArray, totalPages);
    }
    else {
      this.processTextData(jobId, data, pagesArray, totalPages);
    }
  }
  else {
    if (this.options.dataType === dataTypeAtlasBinary) { // eslint-disable-line no-lonely-if
      this.processAtlasBinaryData(jobId, data);
    }
    else {
      this.processBinaryData(jobId, data, pagesArray, totalPages);
    }
  }
};

/*
* headers parameter is an array of object of type {name: "", value: ""}
*/
AtlasHttp.prototype.getData = function (action, jobId, headers = [], optionalBackOffValues = []) {
  let nextId;
  let backOff = [1000, 2000, 2000, 4000];
  if (optionalBackOffValues
    && Array.isArray(optionalBackOffValues)
    && optionalBackOffValues.length > 0) {
    backOff = optionalBackOffValues;
  }
  let backOffIndex = -1;
  const startTime = +new Date();
  const timoutTime = startTime + (this.options.maxLoadTimeSec * 1000);
  const self = this;

  const clientPages = {
    pagesLoaded: 0,
  };

  (async function dataLoop() {
    try {
      if (+new Date() > timoutTime) {
        throw new Error('Max load time exceeded. Aborting open operation');
      }
      if (self.options.continueLoad && typeof self.options.continueLoad === 'function' && self.options.continueLoad() === false) {
        throw new Error('Load aborted by client. Stopping poll for data.');
      }
      const params = [];
      if (nextId) {
        if (self.options.useJrm) {
          params.push(self.makeParam('next-id', nextId));
        }
        else {
          params.push(self.makeParam('nextId', nextId));
        }
      }

      let httpResult = await self.fetchData(action, jobId, params, headers);
      if (httpResult.status === 206) { // Partial data retrieved, fetch whole page
        console.debug(`Partial data received, fetching whole page ${JSON.stringify(params)}`);
        httpResult = await self.getMultichunkData(
          action,
          jobId,
          params,
          headers,
          httpResult.data,
          httpResult.contentRange,
        );
      }
      if (httpResult.pagesReceived > 0) {
        clientPages.pagesLoaded += httpResult.pagesReceived;
        debug('Total pages received:', clientPages.pagesLoaded);
      }

      if (httpResult.status === 200 || httpResult.status === 202) {
        // data may be available in response
        if (!httpResult.nextId) throw new Error('Server did not return nextid for 200 status');
        nextId = httpResult.nextId;
        backOffIndex = -1;
        if (httpResult.data) {
          if (self.options.useJrm) {
            if (httpResult.xJobContentType === 'application/json' && httpResult.data instanceof ArrayBuffer) {
              const decoder = new TextDecoder('utf-8');
              const jsonStream = decoder.decode(httpResult.data);
              // Parse individual JSON objects from stream
              const jsonObjects = jsonStream
                .split(/(?<=\})\s*(?=\{)/g) // smart split between } and {
                .map((s) => JSON.parse(s));
              httpResult.data = JSON.stringify(jsonObjects);
            }
          }
          if (typeof httpResult.data === 'string' && httpResult.data.length > 0) self.processHttpData(jobId, httpResult.data, httpResult.pagesArray, httpResult.totalPages);
          else if (httpResult.data.byteLength > 0) {
            self.processHttpData(
              jobId,
              httpResult.data,
              httpResult.pagesArray,
              httpResult.totalPages,
            );
          }
          else console.log('No data callback made as data is not of expected type', typeof httpResult.data);
        }
        if (self.options.onProgress && httpResult.pageInfo) {
          httpResult.pageInfo.pagesLoaded = clientPages.pagesLoaded;
          self.options.onProgress(httpResult.pageInfo);
        }
        if (self.options.useJrm) {
          console.log('Poll status (jrm)', new Date().toISOString(), httpResult.totalPages, clientPages.pagesLoaded);
          if (httpResult.totalPages !== -1 && httpResult.totalPages === clientPages.pagesLoaded) {
            if (self.options.onComplete) self.options.onComplete(jobId);
            return;
          }
        }
        else {
          console.log('Poll status', new Date().toISOString(), httpResult.pageInfo.totalEnsPages, clientPages.pagesLoaded);
          if (httpResult.pageInfo.totalEnsPages !== -1
            && httpResult.pageInfo.totalEnsPages === clientPages.pagesLoaded) {
            if (self.options.onComplete) self.options.onComplete(jobId);
            return;
          }
        }
        // call within the next half second for more data
        setTimeout(dataLoop, Math.floor(Math.random() * 500));
      }
      else if (httpResult.status === 204) { /* no additional data available.
        keep polling until other status or timeout */
        if (self.options.onProgress && httpResult.pageInfo) { /* update progess alone as there
          could be other info sent in Last-Status header (like bom expansion time etc.) */
          httpResult.pageInfo.pagesLoaded = clientPages.pagesLoaded;
          self.options.onProgress(httpResult.pageInfo);
        }

        if (self.options.useJrm) {
          console.log('Poll status (jrm) (204 response)', new Date().toISOString(), httpResult.totalPages, clientPages.pagesLoaded);
          if (httpResult.totalPages !== -1 && httpResult.totalPages === clientPages.pagesLoaded) {
            if (self.options.onComplete) self.options.onComplete(jobId);
            return;
          }
        }
        else {
          console.log('Poll status (204 response)', new Date().toISOString(), httpResult.pageInfo.totalEnsPages, clientPages.pagesLoaded);
          if (httpResult.pageInfo.totalEnsPages !== -1
            && httpResult.pageInfo.totalEnsPages === clientPages.pagesLoaded) {
            if (self.options.onComplete) self.options.onComplete(jobId);
            return;
          }
        }
        nextId = httpResult.nextId;
        backOffIndex += 1;
        if (backOffIndex >= backOff.length) {
          backOffIndex = backOff.length - 1; // stay at max backoff until there is more data
        }
        // add 250ms jitter
        setTimeout(dataLoop, backOff[backOffIndex] + Math.floor(Math.random() * 250));
      }
      else { // unhandled status. treat as error
        throw new Error(`unknown status received. status: ${httpResult.status}`);
      }
    }
    catch (err) {
      console.log('=== Error getting HTTP data ===', err);
      if (self.options.onError) self.options.onError(jobId, err);
    }
  }());
};

export default { Client: AtlasHttp, AtlasBinaryTypeName: dataTypeAtlasBinary };
